// Pure contract for local task review, the authenticated writer and receipt consumers.
export const TASK_PRIORITIES = ["Critical", "Important", "Normal"] as const;
export const TASK_ACTIONS = ["search", "get", "receipt", "create", "update", "delete", "complete", "reopen", "link_event"] as const;
export const TASK_PERSISTED_FIELDS = ["id", "user_id", "title", "priority", "due_date", "time_label", "done", "created_at", "source", "external_id", "client_action_id", "completed_at"] as const;
export const TASK_SELECT_FIELDS = TASK_PERSISTED_FIELDS.join(",");
export const TASK_FIELD_LIMITS = { title: 1000, time_label: 100, source: 100, external_id: 2000, client_action_id: 1000 } as const;
export type TaskPriority = typeof TASK_PRIORITIES[number];
export type TaskSearchStatus = "open" | "completed" | "all";
export type TaskAction = typeof TASK_ACTIONS[number];
/** link_event is an internal Agenda operation, never a model proposal. */
export type TaskWriteAction = Exclude<TaskAction, "search" | "get" | "receipt">;
export type TaskProposalAction = Exclude<TaskWriteAction, "link_event">;
export interface TaskPersistedRecord {
  id: string; user_id: string; title: string; priority: TaskPriority; due_date: string | null; time_label: string | null;
  done: boolean; created_at: string | null; source: string | null; external_id: string | null;
  client_action_id: string | null; completed_at: string | null;
}
export interface TaskSnapshot { task: TaskPersistedRecord }
export interface TaskRecord extends TaskPersistedRecord {
  writable: boolean; read_only: boolean; content_truncated: boolean; snapshot: TaskSnapshot | null; write_restriction?: string;
}
export interface TaskMirrorContext { session_id: string; action_id: string }
export interface TaskActionRequest {
  action: TaskAction; task_id?: string; operation_id?: string; idempotency_key?: string; query?: string; limit?: number; cursor?: string; status?: TaskSearchStatus;
  title?: string; priority?: TaskPriority; due_date?: string | null; time_label?: string | null; done?: boolean;
  source?: "manual" | "mirror" | "voice"; client_action_id?: string; event_id?: string;
  expected_snapshot?: TaskSnapshot; confirmed?: true; mirror_context?: TaskMirrorContext | null;
}
export type TaskRequest = TaskActionRequest;
export interface TaskWriteResult {
  action: TaskWriteAction; operation_id: string; task_id: string; id: string; evidence: "database_readback";
  task: TaskRecord | null; before?: TaskSnapshot; event_id?: string; unchanged?: boolean; deleted?: true;
  delete_absence?: { task: true }; request_hash: string; mirror_context: TaskMirrorContext | null;
}
export class TaskActionError extends Error {
  constructor(message: string, public status = 400) { super(message); }
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DETAILS = ["title", "priority", "due_date", "time_label", "done"];
const METADATA = ["operation_id", "idempotency_key", "confirmed", "mirror_context"];
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const own = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);
const only = (value: Record<string, unknown>, keys: readonly string[]) => Object.keys(value).every(key => keys.includes(key));
const fail = (name: string): never => { throw new TaskActionError(name); };
export const isTaskWriteAction = (action: string): action is TaskWriteAction => TASK_ACTIONS.includes(action as TaskAction) && !["search", "get", "receipt"].includes(action);
export function taskId(value: unknown): string {
  if (typeof value !== "string" || !UUID.test(value)) return fail("invalid_id");
  return value.toLowerCase();
}
export function taskActionId(value: unknown): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9._:-]{1,160}$/.test(value)) return fail("invalid_action_id");
  return value;
}
export function taskClientActionId(value: unknown): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9._:+/=-]{1,160}$/.test(value)) return fail("invalid_client_action_id");
  return value;
}
export function validTaskActionDate(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value.slice(0, 4) === "0000") return false;
  const parsed = Date.parse(value + "T00:00:00Z");
  return Number.isFinite(parsed) && new Date(parsed).toISOString().slice(0, 10) === value;
}
export function validTaskActionTimestamp(value: unknown): value is string {
  if (typeof value !== "string" || value.length > 40 || !validTaskActionDate(value.slice(0, 10))) return false;
  const match = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?(Z|[+-](\d{2}):(\d{2}))$/.exec(value);
  // timestamptz stores microseconds; reject any input that PostgreSQL would round.
  return !!match && +match[1] <= 23 && +match[2] <= 59 && +match[3] <= 59 && !/[1-9]/.test((match[4] ?? "").slice(6))
    && (!match[6] || (+match[6] <= 14 && +match[7] <= 59 && (+match[6] !== 14 || +match[7] === 0))) && Number.isFinite(Date.parse(value));
}
function timestampIdentity(value: string | null): string | null {
  if (value === null) return null;
  const fraction = /\.(\d+)(?=Z|[+-]\d{2}:\d{2}$)/.exec(value)?.[1] ?? "";
  const whole = value.replace(/\.\d+(?=Z|[+-]\d{2}:\d{2}$)/, "");
  return (BigInt(Date.parse(whole)) * 1_000_000n + BigInt(fraction.padEnd(9, "0") || "0")).toString();
}
export function normalizeTaskDueDate(value: unknown): string | null {
  if (value === null) return null;
  if (validTaskActionDate(value)) return value + "T00:00:00.000Z";
  if (!validTaskActionTimestamp(value)) return fail("invalid_due_date");
  return value;
}
export function taskDueDatesEqual(left: unknown, right: unknown): boolean {
  try { return timestampIdentity(normalizeTaskDueDate(left)) === timestampIdentity(normalizeTaskDueDate(right)); } catch { return false; }
}
function exactText(value: unknown, maximum: number, field: string, multiline = false): string {
  if (typeof value !== "string" || value.length > maximum || [...value].some(char => /\p{Cc}/u.test(char) && !(multiline && ["\n", "\r", "\t"].includes(char)))) return fail("invalid_" + field);
  return value;
}
function context(value: unknown): TaskMirrorContext | null {
  if (value === null || value === undefined) return null;
  if (!object(value) || !only(value, ["session_id", "action_id"]) || !own(value, "session_id") || !own(value, "action_id")) return fail("invalid_mirror_context");
  return { session_id: taskId(value.session_id), action_id: taskActionId(value.action_id) };
}
function persistedTask(raw: unknown, owner?: string): TaskPersistedRecord {
  if (!object(raw) || !only(raw, TASK_PERSISTED_FIELDS) || TASK_PERSISTED_FIELDS.some(field => !own(raw, field))) return fail("invalid_task_snapshot");
  const id = taskId(raw.id), userId = taskId(raw.user_id);
  if (owner && userId !== taskId(owner)) return fail("task_not_owned");
  if (!TASK_PRIORITIES.includes(raw.priority as TaskPriority) || typeof raw.done !== "boolean"
    || ["due_date", "created_at", "completed_at"].some(field => raw[field] !== null && !validTaskActionTimestamp(raw[field]))) return fail("invalid_task_snapshot");
  return { id, user_id: userId, title: exactText(raw.title, TASK_FIELD_LIMITS.title, "title", true), priority: raw.priority as TaskPriority,
    due_date: raw.due_date as string | null, time_label: raw.time_label === null ? null : exactText(raw.time_label, TASK_FIELD_LIMITS.time_label, "time_label"),
    done: raw.done, created_at: raw.created_at as string | null, source: raw.source === null ? null : exactText(raw.source, TASK_FIELD_LIMITS.source, "source"),
    external_id: raw.external_id === null ? null : exactText(raw.external_id, TASK_FIELD_LIMITS.external_id, "external_id"),
    client_action_id: raw.client_action_id === null ? null : exactText(raw.client_action_id, TASK_FIELD_LIMITS.client_action_id, "client_action_id"), completed_at: raw.completed_at as string | null };
}
export function isTaskEditable(task: Pick<TaskPersistedRecord, "source">): boolean { return task.source === null || ["manual", "mirror", "voice"].includes(task.source); }
export function canonicalTaskSnapshot(raw: unknown, owner?: string): TaskSnapshot {
  if (!object(raw) || !only(raw, ["task"])) return fail("invalid_task_snapshot");
  return { task: persistedTask(raw.task, owner) };
}
export function taskSnapshotVersion(snapshot: TaskSnapshot): string {
  const canonical = canonicalTaskSnapshot(snapshot).task;
  return JSON.stringify({ ...canonical, due_date: timestampIdentity(canonical.due_date), created_at: timestampIdentity(canonical.created_at), completed_at: timestampIdentity(canonical.completed_at) });
}
export function taskSnapshotsEqual(left: unknown, right: unknown): boolean {
  try { return taskSnapshotVersion(canonicalTaskSnapshot(left)) === taskSnapshotVersion(canonicalTaskSnapshot(right)); } catch { return false; }
}
function clip(value: string, maximum: number): string {
  const result = value.slice(0, maximum);
  const last = result.charCodeAt(result.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? result.slice(0, -1) : result;
}
export function canonicalTaskRecord(raw: unknown, owner?: string): TaskRecord {
  if (!object(raw) || !only(raw, ["task"]) || !object(raw.task)) return fail("invalid_task_record");
  const bounded = { ...raw.task }; let truncated = false;
  for (const [field, maximum] of Object.entries(TASK_FIELD_LIMITS)) {
    const value = bounded[field];
    // Controls are checked on the original field; a hidden suffix is never ignored.
    if (typeof value === "string") {
      exactText(value, Number.MAX_SAFE_INTEGER, field, field === "title");
      if (value.length > maximum) { bounded[field] = clip(value, maximum); truncated = true; }
    }
  }
  const task = persistedTask(bounded, owner);
  const restriction = truncated ? "task_content_truncated" : !isTaskEditable(task) ? "imported_task_read_only" : !task.title.trim() ? "invalid_task_content" : undefined;
  return { ...task, writable: !restriction, read_only: !!restriction, content_truncated: truncated,
    snapshot: truncated ? null : { task }, ...(restriction ? { write_restriction: restriction } : {}) };
}
export function parseTaskActionRequest(raw: unknown): TaskActionRequest {
  if (!object(raw) || !TASK_ACTIONS.includes(raw.action as TaskAction)) return fail("unsupported_action");
  const action = raw.action as TaskAction, write = isTaskWriteAction(action);
  const fields: Record<TaskAction, string[]> = {
    search: ["query", "limit", "cursor", "status"], get: ["task_id"], receipt: ["operation_id"],
    create: ["task_id", "title", "priority", "due_date", "time_label", "source", "client_action_id"],
    update: ["task_id", ...DETAILS], delete: ["task_id"], complete: ["task_id"], reopen: ["task_id"], link_event: ["task_id", "event_id"],
  };
  if (!only(raw, ["action", "mirror_context", ...fields[action], ...(write ? [...METADATA, ...(action !== "create" ? ["expected_snapshot"] : [])] : [])])) return fail("unsupported_field");
  const request: TaskActionRequest = { action };
  if (raw.mirror_context !== undefined) request.mirror_context = context(raw.mirror_context);
  if (!["search", "receipt"].includes(action)) request.task_id = taskId(raw.task_id);
  if (action === "search") {
    request.query = raw.query === undefined ? "" : exactText(raw.query, 200, "query").trim();
    const limit = raw.limit === undefined ? 10 : raw.limit;
    if (!Number.isInteger(limit) || Number(limit) < 1 || Number(limit) > 10) return fail("invalid_limit");
    request.limit = Number(limit);
    const status = raw.status === undefined ? "open" : raw.status;
    if (!["open", "completed", "all"].includes(status as string)) return fail("invalid_status");
    request.status = status as TaskSearchStatus;
    if (raw.cursor !== undefined) request.cursor = taskId(raw.cursor);
  }
  if (write || action === "receipt") request.operation_id = taskId(raw.operation_id);
  if (!write) return request;
  if (raw.confirmed !== true) return fail("confirmation_required");
  request.confirmed = true;
  if (request.mirror_context?.action_id.toLowerCase() === request.operation_id) return fail("operation_origin_must_differ");
  if (raw.idempotency_key !== undefined) {
    if (raw.idempotency_key !== "mirror_task_" + request.operation_id + "_" + action + "_0") return fail("invalid_idempotency_key");
    request.idempotency_key = raw.idempotency_key as string;
  }
  if (action !== "create") {
    request.expected_snapshot = canonicalTaskSnapshot(raw.expected_snapshot);
    if (request.expected_snapshot.task.id !== request.task_id) return fail("snapshot_target_mismatch");
    const observed = canonicalTaskRecord(request.expected_snapshot);
    if (!observed.writable) return fail(observed.write_restriction ?? "task_read_only");
  }
  if (own(raw, "title")) {
    request.title = exactText(raw.title, TASK_FIELD_LIMITS.title, "title", true).trim();
    if (!request.title) return fail("invalid_title");
  }
  if (own(raw, "priority")) {
    if (!TASK_PRIORITIES.includes(raw.priority as TaskPriority)) return fail("invalid_priority");
    request.priority = raw.priority as TaskPriority;
  }
  if (own(raw, "due_date")) request.due_date = normalizeTaskDueDate(raw.due_date);
  if (own(raw, "time_label")) request.time_label = raw.time_label === null ? null : exactText(raw.time_label, TASK_FIELD_LIMITS.time_label, "time_label");
  if (own(raw, "done")) {
    if (typeof raw.done !== "boolean") return fail("invalid_done");
    request.done = raw.done;
  }
  if (action === "create") {
    if (!request.title) return fail("title_required");
    request.priority ??= "Normal"; request.due_date ??= null; request.time_label ??= null;
    const source = raw.source === undefined ? "manual" : raw.source;
    if (!["manual", "mirror", "voice"].includes(source as string)) return fail("invalid_source");
    request.source = source as TaskActionRequest["source"];
    request.client_action_id = raw.client_action_id === undefined ? "mirror_task_" + request.operation_id + "_create_0" : taskClientActionId(raw.client_action_id);
  }
  if (action === "update" && !DETAILS.some(field => own(raw, field))) return fail("changes_required");
  if (action === "link_event") {
    if (request.mirror_context) return fail("internal_action_only");
    request.event_id = taskId(raw.event_id);
    if (request.expected_snapshot!.task.external_id !== null && request.expected_snapshot!.task.external_id !== request.event_id) return fail("event_link_conflict");
  }
  return request;
}
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (object(value)) return Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])]));
  return value;
}
const same = (left: unknown, right: unknown): boolean => JSON.stringify(stable(left)) === JSON.stringify(stable(right));
function orderedRequestJson(value: unknown): string {
  if (Array.isArray(value)) return "[" + value.map(orderedRequestJson).join(",") + "]";
  if (object(value)) return "{" + Object.keys(value).sort().map(key => JSON.stringify(key) + ":" + orderedRequestJson(value[key])).join(",") + "}";
  return JSON.stringify(value);
}
/** Compares the reviewed write with its persisted request, including its immutable origin. */
export function sameReviewedTaskRequest(body: TaskActionRequest, raw: unknown): boolean {
  try {
    const stored = parseTaskActionRequest(raw);
    if (!isTaskWriteAction(body.action) || !isTaskWriteAction(stored.action)) return false;
    const { idempotency_key: _bodyKey, expected_snapshot: expected, mirror_context: context, due_date: due, ...fields } = body;
    const { idempotency_key: _storedKey, expected_snapshot: storedExpected, mirror_context: storedContext, due_date: storedDue, ...storedFields } = stored;
    return orderedRequestJson(fields) === orderedRequestJson(storedFields) && orderedRequestJson(context ?? null) === orderedRequestJson(storedContext ?? null)
      && (due === undefined ? storedDue === undefined : storedDue !== undefined && taskDueDatesEqual(due, storedDue))
      && (expected === undefined ? storedExpected === undefined : storedExpected !== undefined && taskSnapshotsEqual(expected, storedExpected));
  } catch { return false; }
}
function sameField(left: TaskPersistedRecord, right: TaskPersistedRecord, field: typeof TASK_PERSISTED_FIELDS[number]): boolean {
  return ["due_date", "created_at", "completed_at"].includes(field) ? timestampIdentity(left[field] as string | null) === timestampIdentity(right[field] as string | null) : same(left[field], right[field]);
}
/** Field proof only. The authenticated writer obtains all snapshots through real owner-bound reads. */
export function hasTaskWriteProof(input: unknown, raw: unknown, owner?: string): raw is TaskWriteResult {
  try {
    const request = parseTaskActionRequest(input), action = request.action;
    if (!isTaskWriteAction(action) || !object(raw) || raw.action !== action || raw.operation_id !== request.operation_id || raw.task_id !== request.task_id || raw.id !== request.task_id
      || raw.evidence !== "database_readback" || typeof raw.request_hash !== "string" || !/^[0-9a-f]{32}$/.test(raw.request_hash)
      || !own(raw, "mirror_context") || !same(context(raw.mirror_context), request.mirror_context ?? null) || (raw.unchanged !== undefined && typeof raw.unchanged !== "boolean")) return false;
    const before = request.expected_snapshot;
    if (before && (!taskSnapshotsEqual(before, raw.before) || (owner && before.task.user_id !== taskId(owner)))) return false;
    if (action === "delete") return !!before && raw.deleted === true && raw.task === null && raw.unchanged !== true && object(raw.delete_absence) && only(raw.delete_absence, ["task"]) && raw.delete_absence.task === true;
    if (raw.deleted !== undefined || raw.delete_absence !== undefined || !object(raw.task) || !object(raw.task.snapshot)) return false;
    const after = canonicalTaskSnapshot(raw.task.snapshot, owner), record = canonicalTaskRecord(after, owner);
    if (!same(record, raw.task) || !record.writable || record.id !== request.task_id || record.content_truncated) return false;
    if (action === "create") return record.source === request.source && record.external_id === null && record.client_action_id === request.client_action_id
      && record.done === false && record.completed_at === null && record.title === request.title && record.priority === request.priority
      && taskDueDatesEqual(record.due_date, request.due_date) && record.time_label === request.time_label;
    if (!before) return false;
    const changed = action === "update" ? DETAILS.filter(field => own(request, field)) : ["complete", "reopen"].includes(action) ? ["done"] : action === "link_event" ? ["external_id"] : [];
    if (!TASK_PERSISTED_FIELDS.every(field => changed.includes(field) || (field === "completed_at" && changed.includes("done")) || sameField(before.task, after.task, field))) return false;
    for (const field of changed) {
      const expected = action === "complete" ? true : action === "reopen" ? false : action === "link_event" ? request.event_id : (request as unknown as Record<string, unknown>)[field];
      if (field === "due_date" ? !taskDueDatesEqual(after.task.due_date, expected) : !same((after.task as unknown as Record<string, unknown>)[field], expected)) return false;
    }
    if (changed.includes("done")) {
      if (!after.task.done && after.task.completed_at !== null) return false;
      // The SQL writer assigns NOW on false→true. Field proof cannot compare
      // against a trusted database clock; a coincident old instant is possible.
      if (after.task.done && (before.task.done ? !sameField(before.task, after.task, "completed_at") : after.task.completed_at === null)) return false;
    }
    if (action === "link_event" && raw.event_id !== request.event_id) return false;
    if (raw.unchanged === true && !taskSnapshotsEqual(before, after)) return false;
    return true;
  } catch { return false; }
}
